/**
 * Все названия одним списком.
 *
 * Второй вход к тем же полям, а не второе место хранения. Правка живёт там,
 * где объект показан, — это правило; но расставить имена тридцати объектам,
 * прыгая по пяти вкладкам, невозможно, и без такого списка имена просто не
 * расставят.
 *
 * Пишет теми же методами, что и карандаши на вкладках. Третьего пути записи
 * здесь нет намеренно: он однажды разошёлся бы с двумя первыми.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';

interface NameItem {
  key: string;
  target: 'inventory' | 'agent';
  kind: string;
  kindWord: string;
  factName: string;
  label: string | null;
  note: string | null;
  where: string | null;
  service: string | null;
  role: string | null;
  retired: boolean;
}

interface NamesSnapshot {
  generatedAt: string;
  items: NameItem[];
  named: number;
}

function Row({ item, busy, onSave }: {
  item: NameItem;
  busy: boolean;
  onSave: (item: NameItem, label: string, note: string) => Promise<void>;
}): React.JSX.Element {
  const [label, setLabel] = useState(item.label ?? '');
  const [note, setNote] = useState(item.note ?? '');
  const dirty = label !== (item.label ?? '') || note !== (item.note ?? '');

  return (
    <tr className={item.retired ? 'names__row--off' : ''}>
      <td className="names__kind">{item.kindWord}</td>
      <td className="names__fact mono" title={item.key}>{item.factName}</td>
      <td className="names__where muted">{item.where ?? '—'}</td>
      <td>
        <input
          className="input input--small"
          value={label}
          maxLength={200}
          placeholder={item.factName}
          disabled={busy}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && dirty) void onSave(item, label, note); }}
          aria-label={`Как называть ${item.factName}`}
        />
      </td>
      <td>
        <input
          className="input input--small"
          value={note}
          maxLength={2000}
          placeholder="зачем нужен, что помнить"
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && dirty) void onSave(item, label, note); }}
          aria-label={`Заметка про ${item.factName}`}
        />
      </td>
      <td className="names__save">
        {/*
          Кнопка появляется только у изменённой строки. Восемьдесят кнопок
          «сохранить», из которых нужна одна, — это не список, а поле мин.
        */}
        {dirty && (
          <button type="button" className="button button--small"
                  disabled={busy} onClick={() => void onSave(item, label, note)}>
            Сохранить
          </button>
        )}
      </td>
    </tr>
  );
}

export function Names(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<NamesSnapshot>('names.list');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [onlyUnnamed, setOnlyUnnamed] = useState(false);

  const save = async (item: NameItem, label: string, note: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      // Метод зависит от того, ЧТО переименовывают: агент живёт не в
      // инвентаре, и у него правится подпись, а не имя в DNS.
      if (item.target === 'agent') {
        await request('agents.rename', { kid: item.key, label, note });
      } else {
        await request('inventory.bind', { key: item.key, label, note });
      }
      setNotice(`${item.factName}: сохранено`);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data?.items ?? []).filter((i) => {
      if (onlyUnnamed && i.label) return false;
      if (!q) return true;
      return [i.factName, i.label, i.note, i.where, i.kindWord, i.service]
        .some((v) => v?.toLowerCase().includes(q));
    });
  }, [data, filter, onlyUnnamed]);

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Все названия</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Все названия</h2>
        <p className="hub__lead">
          Имя владельца встаёт рядом с настоящим, а не вместо него: искать объект
          на машине придётся по настоящему.{' '}
          {data && <>Названо {data.named} из {data.items.length}.</>}
        </p>
        <div className="names__bar">
          <input
            className="input input--small names__filter"
            value={filter}
            placeholder="найти по имени, месту или заметке"
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Поиск по списку названий"
          />
          <label className="names__only">
            <input
              type="checkbox"
              checked={onlyUnnamed}
              onChange={(e) => setOnlyUnnamed(e.target.checked)}
            />
            только без названия
          </label>
        </div>
        {failure && <p className="alert alert--error">{failure}</p>}
        {notice && !failure && <p className="alert alert--ok">{notice}</p>}
      </header>

      <div className="panel__scroll">
        {!data ? (
          <p className="muted">{loading ? 'Собираем список…' : 'Нет данных.'}</p>
        ) : (
          <table className="table names__table">
            <thead>
              <tr>
                <th scope="col">Вид</th>
                <th scope="col">На машине</th>
                <th scope="col">Внутри чего</th>
                <th scope="col">Называть</th>
                <th scope="col">Заметка</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <Row key={`${i.target}:${i.key}`} item={i} busy={busy} onSave={save} />
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="muted">Ничего не нашлось.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <footer className="panel__foot">
        <span className="legend__item">пустое поле «называть» возвращает имя с машины</span>
        <span className="legend__item">Enter сохраняет строку</span>
      </footer>
    </section>
  );
}
